import { useMemo, useRef, useState, type ReactNode } from 'react';
import { addUtmToContentLinks } from '~/modules/tracking/util/add-utm-to-content-links';
import { useProjectLinkTracking } from '../hooks/use-project-link-tracking';
import { ArrowUpRight } from 'lucide-react';

import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import { RICH_TEXT_HTML_CLASS } from '~/ui-rocco/pages/detail-shared';
import { htmlToPlainText } from '~/ui-rocco/pages/detail-shared/sentence-offsets';
import { useCropToImage } from '~/ui-rocco/pages/detail-shared/use-crop-to-image';
import { ACHIPRO_PHOTOGRAPHER_SPLIT } from '@modules/project/component/details/util/get-details-items';
import { AboutProjectImage } from './AboutProjectImage';
import { useIsDesktopViewport } from '@rocco/ui/tile-shared/hooks/use-tile-hero-view-transition';

/** One spec-table row, as produced by `getDetailsItems`. */
export interface ProjectSpecItem {
    itemName: string;
    itemNameDesktop?: string;
    value: string;
    link?: string;
}

interface AboutProjectRowContentProps {
    /** Large lead statement — the project's editorial subheading. */
    heading?: string;
    /** Project description as CMS rich-text HTML. */
    contentHtml?: string;
    /** Key/value spec rows (Title / Builder / Category / Completed / …). */
    specItems?: ProjectSpecItem[];
    /** Drawn under the spec table, inside the cropped copy — the engagement stats. */
    afterCopy?: ReactNode;
}

// Dark-theme twin of the product page's spec list (ProductSpecList):
// medium label left, grey value right, a hairline under every row.
const ROW_CLASS =
    'flex items-start border-b border-white/10 first:pt-0 last:border-b-0 max-md:gap-4 max-md:py-2 md:gap-[3.0625rem] md:py-[0.8125rem]';
const LABEL_CLASS =
    'shrink-0 font-sans font-medium leading-[1.3] whitespace-pre-line text-white max-md:w-[7.5rem] max-md:text-sm md:w-[16.1875rem] md:text-[1.375rem]';
/** Text track + fixed arrow track so unlinked rows align with text, not icons. */
const VALUE_CLASS =
    'm-0 grid min-w-0 flex-1 grid-cols-[minmax(0,1fr)_0.85em] items-baseline gap-1 break-words text-right font-sans font-normal leading-[1.3] text-[#a3a3a3] max-md:text-sm md:text-[1.375rem]';
const VALUE_LINE_CLASS =
    'grid w-full grid-cols-[minmax(0,1fr)_0.85em] items-baseline gap-1';
const LINK_CLASS =
    'underline decoration-white/25 underline-offset-2 transition-colors hover:text-white';

/** Grey underline alone is too subtle on the grey value text, so linked values
 *  carry a small arrow (matches the product spec table). Kept in a fixed column
 *  so rows without a link still line up with the text above. */
const LinkArrow = ({ show }: { show: boolean }) => (
    <span
        className="inline-flex size-[0.85em] shrink-0 items-center justify-center"
        aria-hidden={!show}
    >
        {show ? (
            <ArrowUpRight
                aria-hidden="true"
                strokeWidth={2}
                className="size-[0.85em] -translate-y-[0.05em]"
            />
        ) : null}
    </span>
);

/**
 * Renders a spec value: the category crumbs as "/"-joined links, the
 * photographers as stacked (per-person) links, and any other linked value as a
 * single link — all with the shared underline + arrow affordance.
 */
const SpecValue = ({ item }: { item: ProjectSpecItem }): ReactNode => {
    if (item.itemName === 'Category' && item.link) {
        const crumbs = item.value.split(',');
        const links = item.link.split(',');
        return (
            <>
                <span className="min-w-0 text-right">
                    {crumbs.map((crumb, index) => {
                        const to = links[index];
                        return (
                            <span key={`${crumb}-${index}`}>
                                {index > 0 ? (
                                    <span className="text-white/40"> / </span>
                                ) : null}
                                {to ? (
                                    <RoccoLink to={to} className={LINK_CLASS}>
                                        {crumb}
                                    </RoccoLink>
                                ) : (
                                    crumb
                                )}
                            </span>
                        );
                    })}
                </span>
                <LinkArrow show />
            </>
        );
    }

    if (item.itemName === 'Photographers') {
        const names = item.value.split(ACHIPRO_PHOTOGRAPHER_SPLIT);
        const links = (item.link ?? '').split(ACHIPRO_PHOTOGRAPHER_SPLIT);
        return (
            <span className="col-span-2 flex flex-col">
                {names.map((name, index) => {
                    const to = links[index];
                    return to ? (
                        <RoccoLink
                            key={`${name}-${index}`}
                            to={to}
                            className={cn(LINK_CLASS, VALUE_LINE_CLASS)}
                        >
                            <span className="min-w-0 text-right">{name}</span>
                            <LinkArrow show />
                        </RoccoLink>
                    ) : (
                        <span
                            key={`${name}-${index}`}
                            className={VALUE_LINE_CLASS}
                        >
                            <span className="min-w-0 text-right">{name}</span>
                            <LinkArrow show={false} />
                        </span>
                    );
                })}
            </span>
        );
    }

    if (item.link) {
        return (
            <RoccoLink
                to={item.link}
                className={cn(LINK_CLASS, 'col-span-2', VALUE_LINE_CLASS)}
            >
                <span className="min-w-0 text-right">{item.value}</span>
                <LinkArrow show />
            </RoccoLink>
        );
    }

    return (
        <>
            <span className="min-w-0 text-right">{item.value}</span>
            <LinkArrow show={false} />
        </>
    );
};

/** The prose's type — Figma 2354:59747: 20px, 400, leading 1.3; 16px body on mobile. */
const PROSE_CLASS =
    'leading-[1.3] font-normal max-md:text-base md:text-[1.25rem]';

/**
 * About the Project: a large lead statement over the project's description
 * prose beside the project's image, then the project's spec table (Title /
 * Builder / Category / Completed / Price range / …) in the same label-left,
 * value-right layout as the product page's (01) Product Details, and the
 * engagement stats under it. Set in the right-hand editorial column per the
 * June 2026 Figma (2354:59741). The column stops at the foot of the image:
 * the description is cut at the end of a sentence with Read more on its
 * heels, and Read more lets the whole of it run on.
 */
export const AboutProjectRowContent = ({
    heading,
    contentHtml,
    specItems = [],
    afterCopy,
}: AboutProjectRowContentProps) => {
    const desktop = useIsDesktopViewport();
    const trackContentLink = useProjectLinkTracking();
    const [expanded, setExpanded] = useState(false);
    const plainText = useMemo(
        () => htmlToPlainText(contentHtml ?? ''),
        [contentHtml],
    );
    const imageRef = useRef<HTMLDivElement>(null);
    const { copyRef, proseRef, twinRef, limit, crop } = useCropToImage(
        imageRef,
        desktop && !expanded,
        plainText,
    );
    if (!heading && !contentHtml && specItems.length === 0) return null;

    const measuring = desktop && !expanded && crop === null;
    const cropped = desktop && !expanded && crop !== null;
    const proseClass = cn(
        PROSE_CLASS,
        // heading→prose gap ≈ 92px (Figma 2354:59745/59747).
        heading && 'max-md:mt-4 md:mt-[5.75rem]',
    );

    return (
        // Both columns sit in flow, so the foot is measured from whichever
        // column is taller.
        <div className="md:flex md:gap-[6.25rem]">
            {/* `self-start`, or the column stretches to the row and the cap
                measures the copy's own height rather than the picture's. */}
            <div
                ref={imageRef}
                className="md:w-[41.95%] md:shrink-0 md:self-start"
            >
                <AboutProjectImage />
            </div>
            <div className="relative w-full font-sans text-white md:mr-[8.45%] md:w-[calc(49.6%-6.25rem)]">
                {/* The plain text at the column's width, which is what the
                    sentence ends are measured against. Off the page's own
                    flow, and never shown. */}
                {measuring ? (
                    <p
                        ref={twinRef}
                        aria-hidden="true"
                        className={cn(
                            PROSE_CLASS,
                            'pointer-events-none absolute top-0 left-0 m-0 w-full whitespace-pre-line opacity-0',
                        )}
                    >
                        {plainText}
                    </p>
                ) : null}
                <div
                    ref={copyRef}
                    style={
                        measuring && limit !== null
                            ? { maxHeight: limit }
                            : undefined
                    }
                    className={cn(measuring && 'overflow-hidden')}
                >
                    {heading ? (
                        // Lead statement, in the summary row's own setting —
                        // the same 33px medium the description under the
                        // banner is set in, so the two read as one voice.
                        // Mobile drops to the 24px accordion-row heading scale.
                        <p className="m-0 font-sans leading-[1.25] font-medium tracking-[-0.025em] text-[#e5e5e5] max-md:text-[1.5rem] md:text-[2.0625rem]">
                            {heading}
                        </p>
                    ) : null}
                    {cropped ? (
                        // The description to the end of a sentence, and Read
                        // more on the same line: the toggle continues the
                        // sentence rather than sitting under it as a control.
                        <p
                            ref={
                                proseRef as React.RefObject<HTMLParagraphElement>
                            }
                            className={cn(
                                proseClass,
                                'm-0 whitespace-pre-line',
                            )}
                        >
                            {plainText.slice(0, crop.at).trimEnd()}{' '}
                            <button
                                type="button"
                                onClick={() => setExpanded(true)}
                                className="inline cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[#a3a3a3] transition-colors hover:text-[#e5e5e5]"
                            >
                                Read more
                            </button>
                        </p>
                    ) : contentHtml ? (
                        <div
                            ref={proseRef as React.RefObject<HTMLDivElement>}
                            className={cn(proseClass, RICH_TEXT_HTML_CLASS)}
                            // The reader's own id rides on the click: it is
                            // only known in the browser and names them, so it
                            // cannot be part of cached markup the way the UTM
                            // parameters are. `auxClick` for the middle button,
                            // `contextMenu` so Copy link address takes it too.
                            onClick={trackContentLink}
                            onAuxClick={trackContentLink}
                            onContextMenu={trackContentLink}
                            dangerouslySetInnerHTML={{
                                __html: addUtmToContentLinks(contentHtml),
                            }}
                        />
                    ) : null}
                    {!cropped && specItems.length > 0 ? (
                        <dl
                            className={cn(
                                'm-0',
                                (heading || contentHtml) &&
                                    'max-md:mt-8 md:mt-[3.75rem]',
                            )}
                        >
                            {specItems.map((item, index) => (
                                <div
                                    key={`${item.itemName}-${index}`}
                                    className={ROW_CLASS}
                                >
                                    <dt className={LABEL_CLASS}>
                                        {item.itemNameDesktop ?? item.itemName}
                                    </dt>
                                    <dd className={VALUE_CLASS}>
                                        <SpecValue item={item} />
                                    </dd>
                                </div>
                            ))}
                        </dl>
                    ) : null}
                    {!cropped && afterCopy ? (
                        <div className="max-md:mt-6 md:mt-[2.5rem]">
                            {afterCopy}
                        </div>
                    ) : null}
                    {expanded ? (
                        // The way back, at the foot of everything Read more
                        // let out, in the same voice.
                        <button
                            type="button"
                            onClick={() => setExpanded(false)}
                            className={cn(
                                PROSE_CLASS,
                                'mt-[1.5rem] block cursor-pointer border-0 bg-transparent p-0 text-left text-[#a3a3a3] transition-colors hover:text-[#e5e5e5]',
                            )}
                        >
                            Read less
                        </button>
                    ) : null}
                </div>
            </div>
        </div>
    );
};
